import { useSession } from '@context/sessionProvider';
import { DeleteTimerIcon } from '@icons/delete-timer-icon';
import { EditTimerIcon } from '@icons/edit-timer-icon';
import { VerticalDotsIcon } from '@icons/vertical-dots-icon';
import { sendDataAPI } from '@lib/sendDataAPI';
import {
	Button,
	Chip,
	Dropdown,
	DropdownItem,
	DropdownMenu,
	DropdownTrigger,
	Modal,
	ModalBody,
	ModalContent,
	ModalFooter,
	ModalHeader,
	Table,
	TableBody,
	TableCell,
	TableColumn,
	TableHeader,
	TableRow,
	Tooltip,
} from '@nextui-org/react';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { KeyedMutator } from 'swr';
import { ConfigData, GridConfig, GridResponse } from '@/types';

type ColumnKey = keyof Pick<ConfigData, 'name' | 'priority'> | 'enable_timer' | 'actions';

type Column = {
	name: string;
	uid: ColumnKey;
};

type GridTableProps = {
	data: GridConfig[];
	mutate: KeyedMutator<GridResponse>;
	isEmpty: boolean;
};

const columns: Column[] = [
	{ name: 'NAME', uid: 'name' },
	{ name: 'PRIORITY', uid: 'priority' },
	{ name: 'STATUS', uid: 'enable_timer' },
	{ name: 'ACTION', uid: 'actions' },
];

export default function GridTable({ data, mutate, isEmpty }: GridTableProps) {
	const router = useRouter();
	const { context } = useSession();
	const [isDeleting, setIsDeleting] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [currentRowId, setCurrentRowId] = useState<number | null>(null);

	const handleDeleteClick = (id: number) => {
		setCurrentRowId(id);
		setIsModalOpen(true);
	};

	const handleCancelDelete = () => {
		setIsModalOpen(false);
	};

	const handleConfirmDelete = async () => {
		if (currentRowId === null) {
			return;
		}

		setIsDeleting(true);

		const response = await sendDataAPI({ jwtToken: context }, `/api/config/${currentRowId}`, 'DELETE');

		if (response.ok) {
			await mutate();
			toast.success('Timer deleted successfully.');
		} else {
			toast.error('Failed to delete the timer. Please try again.');
		}

		setIsDeleting(false);
		setIsModalOpen(false);
	};

	const renderCell = (row: GridConfig, columnKey: ColumnKey) => {
		switch (columnKey) {
			case 'name':
				return <p className='break-words'>{row.name}</p>;
			case 'priority':
				return <p className='break-words'>{row.priority}</p>;
			case 'enable_timer':
				return (
					<Chip className='capitalize' color={row.enable_timer ? 'success' : 'default'} size='sm' variant='flat'>
						{row.enable_timer ? 'Enabled' : 'Disabled'}
					</Chip>
				);
			case 'actions':
				return (
					<div className='relative flex items-center gap-2'>
						<Dropdown className='lg:hidden'>
							<DropdownTrigger className='lg:hidden'>
								<Button isIconOnly size='sm' variant='light'>
									<VerticalDotsIcon className='text-default-300 rotate-90' width={24} height={24} />
								</Button>
							</DropdownTrigger>
							<DropdownMenu aria-label='Timer actions'>
								<DropdownItem key='edit' onPress={() => router.push(`/edit/${row.id}`)}>
									<div className='flex items-center gap-1.5'>
										<EditTimerIcon />
										<p>Edit</p>
									</div>
								</DropdownItem>
								<DropdownItem
									key='delete'
									className='text-danger'
									color='danger'
									onPress={() => handleDeleteClick(row.id)}
								>
									<div className='flex items-center gap-1.5'>
										<DeleteTimerIcon />
										<p>Delete</p>
									</div>
								</DropdownItem>
							</DropdownMenu>
						</Dropdown>

						<div className='hidden lg:flex gap-2'>
							<Tooltip content='Edit timer'>
								<button
									type='button'
									aria-label='Edit timer'
									className='text-lg text-default-400 cursor-pointer active:opacity-50'
									onClick={() => router.push(`/edit/${row.id}`)}
								>
									<EditTimerIcon />
								</button>
							</Tooltip>
							<Tooltip color='danger' content='Delete timer'>
								<button
									type='button'
									aria-label='Delete timer'
									className='text-lg text-danger cursor-pointer active:opacity-50'
									onClick={() => handleDeleteClick(row.id)}
								>
									<DeleteTimerIcon />
								</button>
							</Tooltip>
						</div>
					</div>
				);
			default:
				return null;
		}
	};

	if (isEmpty) {
		return (
			<div className='flex flex-col items-center justify-center gap-3 py-10 text-center'>
				<p className='font-bold text-base leading-6'>No timers yet</p>
				<p className='font-normal text-base leading-6 text-default-500 max-w-[520px]'>Create your first timer.</p>
			</div>
		);
	}

	return (
		<div>
			<Table removeWrapper aria-label='Table of your timers' className='table-fixed w-full'>
				<TableHeader columns={columns}>
					{(column: Column) => (
						<TableColumn key={column.uid} className='w-1/5'>
							{column.name}
						</TableColumn>
					)}
				</TableHeader>
				<TableBody items={data}>
					{(item: GridConfig) => (
						<TableRow key={item.id} className='h-[61px]'>
							{(columnKey) => (
								<TableCell className='overflow-hidden max-w-0'>{renderCell(item, columnKey as ColumnKey)}</TableCell>
							)}
						</TableRow>
					)}
				</TableBody>
			</Table>
			<Modal isOpen={isModalOpen} onOpenChange={handleCancelDelete}>
				<ModalContent>
					{(onClose) => (
						<>
							<ModalHeader className='flex flex-col gap-1'>Delete this timer?</ModalHeader>
							<ModalBody>
								<p>Are you sure you want to delete this timer?</p>
							</ModalBody>
							<ModalFooter>
								<Button
									isDisabled={isDeleting}
									color='default'
									onPress={() => {
										onClose();
										handleCancelDelete();
									}}
								>
									Cancel
								</Button>
								<Button isLoading={isDeleting} onPress={handleConfirmDelete} color='danger'>
									Delete
								</Button>
							</ModalFooter>
						</>
					)}
				</ModalContent>
			</Modal>
		</div>
	);
}
